import { DateTime } from 'luxon'

import { MARKET_TIMEZONE } from '@/features/bidding/constants'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { BidLevelInput, VolumeCapacityConfig } from '@/features/bidding/types/bidLadderInput'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'

const WATTS_TO_MW = 1_000_000

function matchesDirection(programName: string, direction: 'up' | 'down' | 'symmetric'): boolean {
  if (direction === 'symmetric') return true
  const isDown = programName.includes('_DOWN')
  return direction === 'down' ? isDown : !isDown
}

/**
 * Extracts the market-timezone hour from an ISO 8601 PTU start string.
 */
export function getPtuHour(start: string): number {
  return DateTime.fromISO(start, { zone: MARKET_TIMEZONE }).hour
}

/**
 * Builds a Map of hour -> MW from aggregate suggested bid data for a given direction.
 * For symmetrical markets, all programs are included (no direction filtering).
 */
export function buildAggregateHourlyMap(
  aggregateData: AggregateSuggestedBidResponse | AggregateAvailabilityResponse | null | undefined,
  direction: 'up' | 'down' | 'symmetric',
): Map<number, number> {
  const hourlyMap = new Map<number, number>()
  if (!aggregateData) return hourlyMap

  const entries = 'suggestions' in aggregateData ? aggregateData.suggestions : aggregateData.availabilities
  if (!entries) return hourlyMap

  for (const entry of entries) {
    if (!matchesDirection(entry.marketProgram as string, direction)) continue

    for (const ptuEntry of entry.ptus) {
      const hour = getPtuHour(ptuEntry.ptu.start)
      const volumeMW = ptuEntry.volume.quantity / WATTS_TO_MW
      hourlyMap.set(hour, (hourlyMap.get(hour) ?? 0) + volumeMW)
    }
  }

  return hourlyMap
}

/**
 * Rounds a value down to the nearest granularity step.
 * E.g. roundDownToGranularity(1.37, 0.1) => 1.3
 * Uses precision correction to avoid floating point artifacts.
 */
function roundDownToGranularity(value: number, granularityMW: number): number {
  const steps = Math.floor(value / granularityMW)
  // Determine decimal places from granularity to correct floating point drift
  const decimalPlaces = granularityMW < 1 ? Math.ceil(-Math.log10(granularityMW)) : 0
  return Number((steps * granularityMW).toFixed(decimalPlaces))
}

/**
 * Resolves a VolumeCapacityConfig to MW for a specific hour.
 * For percentage modes, the result is rounded down to the market's volume granularity.
 */
export function resolveVolumeCapacityForHour(
  config: VolumeCapacityConfig | undefined,
  hour: number,
  suggestedMap: Map<number, number>,
  availableMap: Map<number, number>,
  granularityMW = 0.1,
): number {
  if (config?.value == null) return 0

  const value = config.value

  switch (config.mode) {
    case 'fixed':
      return value
    case 'percent_of_suggested':
      return roundDownToGranularity((value / 100) * (suggestedMap.get(hour) ?? 0), granularityMW)
    case 'percent_of_available':
      return roundDownToGranularity((value / 100) * (availableMap.get(hour) ?? 0), granularityMW)
    default:
      return 0
  }
}

/**
 * Resolves average MW across all 24 hours (for overview charts).
 */
export function resolveVolumeCapacityAverage(
  config: VolumeCapacityConfig | undefined,
  suggestedMap: Map<number, number>,
  availableMap: Map<number, number>,
  granularityMW = 0.1,
): number {
  if (config?.value == null) return 0

  if (config.mode === 'fixed') return config.value

  let total = 0
  for (let hour = 0; hour < 24; hour++) {
    total += resolveVolumeCapacityForHour(config, hour, suggestedMap, availableMap, granularityMW)
  }
  return total / 24
}

/**
 * Checks if the total allocated volume for a single hour exceeds the available volume in either direction.
 * Returns false when no availability data exists for a direction (availableUp/Down is 0).
 */
export function isHourOverAllocated(
  hourTotalUp: number,
  hourTotalDown: number,
  availableUp: number,
  availableDown: number,
): boolean {
  return (availableUp > 0 && hourTotalUp > availableUp) || (availableDown > 0 && hourTotalDown > availableDown)
}

/**
 * Checks if any PTU hour has total allocated volume exceeding the available volume.
 * Used for FCR markets (without activation groups) to validate volume capacity before reviewing bids.
 */
export function hasAnyOverAllocatedVolume({
  levels,
  isSymmetrical,
  aggregateSuggestedBids,
  aggregateAvailability,
  granularityMW,
}: {
  levels: BidLevelInput[] | BidLevelInputSymmetrical[]
  isSymmetrical: boolean
  aggregateSuggestedBids: AggregateSuggestedBidResponse | null | undefined
  aggregateAvailability: AggregateAvailabilityResponse | null | undefined
  granularityMW: number
}): boolean {
  const suggestedUpMap = buildAggregateHourlyMap(aggregateSuggestedBids, 'up')
  const suggestedDownMap = isSymmetrical ? suggestedUpMap : buildAggregateHourlyMap(aggregateSuggestedBids, 'down')
  const availableUpMap = buildAggregateHourlyMap(aggregateAvailability, 'up')
  const availableDownMap = isSymmetrical ? availableUpMap : buildAggregateHourlyMap(aggregateAvailability, 'down')

  for (let hour = 0; hour < 24; hour++) {
    let hourTotalUp = 0
    let hourTotalDown = 0

    for (const level of levels) {
      if (isSymmetrical) {
        const symLevel = level as BidLevelInputSymmetrical
        const resolved = resolveVolumeCapacityForHour(
          symLevel.volumeCapacity,
          hour,
          suggestedUpMap,
          availableUpMap,
          granularityMW,
        )
        hourTotalUp += resolved
        hourTotalDown += resolved
      } else {
        const dirLevel = level as BidLevelInput
        hourTotalUp += resolveVolumeCapacityForHour(
          dirLevel.upVolumeCapacity,
          hour,
          suggestedUpMap,
          availableUpMap,
          granularityMW,
        )
        hourTotalDown += resolveVolumeCapacityForHour(
          dirLevel.downVolumeCapacity,
          hour,
          suggestedDownMap,
          availableDownMap,
          granularityMW,
        )
      }
    }

    if (
      isHourOverAllocated(hourTotalUp, hourTotalDown, availableUpMap.get(hour) ?? 0, availableDownMap.get(hour) ?? 0)
    ) {
      return true
    }
  }

  return false
}
